import { Modal } from '@lifebit-ai/lds'
import React from 'react'

import { DataAccessRequestAttachmentSubmission } from 'services/data-access-requests/enums'
import { type Result } from 'services/results/types'

import FilePreviewModal from './FilePreviewModal'
import { FormField } from './FormField'
import * as styles from './ViewFormModal.styles'
import useViewFormModal from './useViewFormModal'
import type { VerificationAttachmentSignedUrlSource } from './useViewFormModal'

type CustomFormSubmission = NonNullable<Result['formSubmission']>

interface ViewFormModalProps {
  open: boolean
  onClose: () => void
  formSubmission?: CustomFormSubmission
  resultId?: string
  resultBucket?: string
  verificationAttachmentSignedUrlSource?: VerificationAttachmentSignedUrlSource
  attachmentSubmission?: DataAccessRequestAttachmentSubmission
}

const ViewFormModal = ({
  open,
  onClose,
  formSubmission,
  resultId,
  resultBucket,
  verificationAttachmentSignedUrlSource,
  attachmentSubmission
}: ViewFormModalProps) => {
  const {
    title,
    formLogoUrl,
    blocks,
    hasSubmission,
    selectedFileToPreview,
    selectedAttachmentPreview,
    selectedSignedUrlPreview,
    isPreviewOpen,
    previewTitle,
    verificationAttachmentSignedUrlQuery,
    onPreviewFile,
    onClosePreview
  } = useViewFormModal({
    formSubmission,
    resultId,
    resultBucket,
    verificationAttachmentSignedUrlSource,
    attachmentSubmission
  })

  return (
    <>
      <Modal onCancel={onClose} open={open} title={title} size="large" closable cancelText="Close">
        {hasSubmission ? (
          <styles.FormContent data-testid="appeal-form-content">
            {formLogoUrl ? (
              <styles.LogoContainer>
                <styles.Logo src={formLogoUrl} alt="Form logo" />
              </styles.LogoContainer>
            ) : null}
            {blocks.map(block => {
              return (
                <styles.Block key={block.id}>
                  {block.title ? <styles.BlockTitle>{block.title}</styles.BlockTitle> : null}
                  <styles.BlockFields>
                    {block.fields.map(field => (
                      <FormField key={field.id} field={field} onPreviewFile={onPreviewFile} />
                    ))}
                  </styles.BlockFields>
                </styles.Block>
              )
            })}
          </styles.FormContent>
        ) : (
          <styles.EmptyFormMessage data-testid="appeal-form-empty-message">
            No custom form data is available for this request.
          </styles.EmptyFormMessage>
        )}
      </Modal>

      <FilePreviewModal
        open={isPreviewOpen}
        title={previewTitle}
        resultId={resultId}
        selectedAttachmentPreview={selectedAttachmentPreview}
        selectedFileToPreview={selectedFileToPreview}
        selectedSignedUrlPreview={selectedSignedUrlPreview}
        verificationAttachmentSignedUrlQuery={verificationAttachmentSignedUrlQuery}
        onClose={onClosePreview}
      />
    </>
  )
}

export default ViewFormModal
